import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { HttpClientError, IDefaultResponseMessage, IQuerySettings, QueryKeys, getQuerySettings } from '@dw/models/shared';
import { ICreateNoticeRequest, INotice, IReorderNoticesRequest, IUpdateNoticeRequest } from '@dw/models/notice';
import { IPreSignedBlobUrl } from '@dw/models/blob';
import { useHttpClient, useSnackbar } from '@dw/hooks';

const BASE_URL = '/v1/dance-schools/notices';

/**
 * Every notice on the school's board, in every state.
 *
 * Deliberately not filtered to what is showing: the person arranging a board needs to see the
 * notice scheduled for next week and the one that expired last term, which are exactly the two a
 * live-only view would hide.
 */
export const useGetNotices = (settings?: IQuerySettings<INotice[]>) => {
  const { GET } = useHttpClient();

  return useQuery<INotice[], HttpClientError>({
    queryKey: [QueryKeys.Notices],
    queryFn: () => GET<INotice[]>(BASE_URL),
    ...getQuerySettings(settings)
  });
};

export const useCreateNoticeMutation = (settings?: IQuerySettings<INotice, ICreateNoticeRequest>) => {
  const { POST } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const querySettings = getQuerySettings(settings);

  return useMutation<INotice, HttpClientError, ICreateNoticeRequest>({
    mutationFn: (request) => POST<INotice>(BASE_URL, request),
    ...querySettings,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Notices] });
      showSnackbar('Notice pinned to the board');

      querySettings.onSuccess?.(response, input);
    }
  });
};

export const useUpdateNoticeMutation = (settings?: IQuerySettings<INotice, IUpdateNoticeRequest>) => {
  const { PUT } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const querySettings = getQuerySettings(settings);

  return useMutation<INotice, HttpClientError, IUpdateNoticeRequest>({
    mutationFn: ({ noticeId, ...request }) => PUT<INotice>(`${BASE_URL}/${noticeId}`, request),
    ...querySettings,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Notices] });
      showSnackbar('Notice updated');

      querySettings.onSuccess?.(response, input);
    }
  });
};

export const useDeleteNoticeMutation = (settings?: IQuerySettings<IDefaultResponseMessage, string>) => {
  const { DELETE } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const querySettings = getQuerySettings(settings);

  return useMutation<IDefaultResponseMessage, HttpClientError, string>({
    mutationFn: (noticeId) => DELETE<IDefaultResponseMessage>(`${BASE_URL}/${noticeId}`),
    ...querySettings,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Notices] });
      showSnackbar('Notice removed from the board');

      querySettings.onSuccess?.(response, input);
    }
  });
};

/**
 * Saves the board order.
 *
 * The request carries the COMPLETE ordered list rather than "move X to position N", which is what
 * makes it idempotent and un-driftable. The response is the whole board, so a client that had gone
 * stale (a notice added or deleted underneath it) resyncs from the save itself.
 *
 * No snackbar: reordering fires on every drop, and a toast per drag would be noise for something
 * the user can already see happen.
 */
export const useReorderNoticesMutation = (settings?: IQuerySettings<INotice[], IReorderNoticesRequest>) => {
  const { PUT } = useHttpClient();
  const queryClient = useQueryClient();

  const querySettings = getQuerySettings(settings);

  return useMutation<INotice[], HttpClientError, IReorderNoticesRequest>({
    mutationFn: (request) => PUT<INotice[]>(`${BASE_URL}/order`, request),
    ...querySettings,
    onSuccess: (response, input) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Notices] });

      querySettings.onSuccess?.(response, input);
    }
  });
};

/**
 * A short-lived signed link to one attachment, minted on click.
 *
 * Never up front: these expire in about fifteen minutes, so pre-minting a board's worth would spend
 * a request each on links that mostly go stale unused.
 */
export const useGetNoticeAttachmentDownloadLink = () => {
  const { GET } = useHttpClient();

  return (noticeId: string, attachmentKey: string) =>
    GET<IPreSignedBlobUrl>(`${BASE_URL}/${noticeId}/attachments/${encodeURIComponent(attachmentKey)}`);
};
